import {Box, Grid, IconButton, Paper} from '@mui/material';
import MDTypography from 'components/MDTypography';
import {useEffect, useState} from 'react';
import DeviceTabs from './DeviceTabs';
import Dropzone from 'react-dropzone';
import {Add, Delete} from '@mui/icons-material';

const DeviceGalleryUploader = ({
  value = [],
  onChange,
  label,
  files = {},
  onFilesChange,
  error,
  helperText,
  required = false,
  deviceTypes = ['mobile', 'tablet', 'desktop'],
}) => {
  const [activeDevice, setActiveDevice] = useState(deviceTypes[2] || 'desktop');
  const [filePreviews, setFilePreviews] = useState({});

  const safeValue = Array.isArray(value) ? value : [];

  useEffect(() => {
    const previews = {};
    Object.entries(files).forEach(([device, fileList]) => {
      if (fileList && Array.isArray(fileList) && fileList.length) {
        previews[device] = fileList.map(file => URL.createObjectURL(file));
      }
    });
    setFilePreviews(previews);

    return () => {
      Object.values(previews).forEach(devicePreviews => {
        if (Array.isArray(devicePreviews)) {
          devicePreviews.forEach(url => URL.revokeObjectURL(url));
        }
      });
    };
  }, [files]);

  const handleDrop = (acceptedFiles, device) => {
    if (!acceptedFiles.length) return;

    const currentFiles = files[device] || [];
    const updatedFiles = [...currentFiles, ...acceptedFiles];

    const updatedFilesMap = {
      ...files,
      [device]: updatedFiles,
    };

    if (onFilesChange) {
      onFilesChange(updatedFilesMap);
    }

    const newItems = acceptedFiles.map(() => ({
      [device]: '',
    }));

    onChange([...safeValue, ...newItems]);
  };

  const removeExistingImage = (imageIndex, device) => {
    const updatedValue = safeValue
      .map((item, idx) => {
        if (idx === imageIndex) {
          const newItem = {...item};
          delete newItem[device];
          return newItem;
        }
        return item;
      })
      .filter(item => item && Object.keys(item).length > 0);

    onChange(updatedValue);
  };

  const removeNewImage = (fileIndex, device) => {
    const currentFiles = files[device] || [];
    const updatedFiles = currentFiles.filter((_, i) => i !== fileIndex);

    const updatedFilesMap = {
      ...files,
      [device]: updatedFiles,
    };
    onFilesChange(updatedFilesMap);
  };

  const imagesWithActiveDevice = safeValue.filter(
    item => item && item[activeDevice],
  );

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={1}>
        <MDTypography variant="caption" color={error ? 'error' : 'text'}>
          {label} {required && <span style={{color: 'red'}}>*</span>}
        </MDTypography>
      </Box>

      <DeviceTabs
        activeDevice={activeDevice}
        onDeviceChange={setActiveDevice}
        deviceTypes={deviceTypes}
      />

      <Dropzone
        onDrop={files => handleDrop(files, activeDevice)}
        accept={{'image/*': []}}
        multiple>
        {({getRootProps, getInputProps}) => (
          <Paper
            {...getRootProps()}
            sx={{
              p: 2,
              border: '2px dashed',
              borderColor: error ? 'error.main' : 'grey.300',
              textAlign: 'center',
              cursor: 'pointer',
              '&:hover': {borderColor: 'primary.main'},
              mb: 2,
            }}>
            <input {...getInputProps()} />
            <Add sx={{fontSize: 40, color: 'text.secondary'}} />
            <MDTypography variant="body2" color="text">
              Add {activeDevice} images to gallery
            </MDTypography>
          </Paper>
        )}
      </Dropzone>

      {imagesWithActiveDevice.length > 0 && (
        <Box mb={2}>
          <MDTypography variant="caption" color="textSecondary" gutterBottom>
            Gallery Images ({activeDevice} view):
          </MDTypography>
          <Grid container spacing={1}>
            {imagesWithActiveDevice.map(item => {
              const index = safeValue.indexOf(item);

              return (
                <Grid
                  item
                  sx={{p: 1, position: 'relative'}}
                  xs={4}
                  sm={3}
                  md={2}
                  key={`${activeDevice}-${index}`}>
                  <img
                    src={item[activeDevice]}
                    alt={`Gallery ${index}`}
                    style={{
                      width: '100%',
                      height: 80,
                      objectFit: 'cover',
                      borderRadius: 4,
                    }}
                  />
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => removeExistingImage(index, activeDevice)}
                    sx={{
                      position: 'absolute',
                      top: 2,
                      right: 2,
                      bgcolor: 'rgba(255,255,255,0.9)',
                    }}>
                    <Delete fontSize="small" />
                  </IconButton>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      )}

      {files[activeDevice] &&
        Array.isArray(files[activeDevice]) &&
        files[activeDevice].length > 0 && (
          <Box>
            <MDTypography variant="caption" color="success" gutterBottom>
              New {activeDevice} images (to be uploaded):
            </MDTypography>
            <Grid container spacing={1}>
              {files[activeDevice].map((file, index) => (
                <Grid item xs={4} sm={3} md={2} key={index}>
                  <Paper sx={{p: 0.5, position: 'relative'}}>
                    <img
                      src={filePreviews[activeDevice]?.[index]}
                      alt=""
                      style={{
                        width: '100%',
                        height: 80,
                        objectFit: 'cover',
                        borderRadius: 4,
                      }}
                    />
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => removeNewImage(index, activeDevice)}
                      sx={{
                        position: 'absolute',
                        top: 2,
                        right: 2,
                        bgcolor: 'rgba(255,255,255,0.9)',
                      }}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

      {error && (
        <MDTypography
          variant="caption"
          color="error"
          sx={{mt: 0.5, display: 'block'}}>
          {helperText}
        </MDTypography>
      )}
    </Box>
  );
};

export default DeviceGalleryUploader;

// const DeviceGalleryUploader = ({
//   value = [],
//   onChange,
//   label,
//   files = {},
//   onFilesChange,
//   error,
//   helperText,
// }) => {
//   const [activeDevice, setActiveDevice] = useState('desktop');
//   const [filePreviews, setFilePreviews] = useState({});

//   useEffect(() => {
//     // Generate previews for new files
//     const previews = {};
//     Object.entries(files).forEach(([device, fileList]) => {
//       if (fileList && fileList.length) {
//         previews[device] = fileList.map(file => URL.createObjectURL(file));
//       }
//     });
//     setFilePreviews(previews);

//     return () => {
//       Object.values(previews).forEach(devicePreviews => {
//         devicePreviews?.forEach(url => URL.revokeObjectURL(url));
//       });
//     };
//   }, [files]);

//   const handleDrop = (acceptedFiles, device) => {
//     if (!acceptedFiles.length) return;

//     const currentFiles = files[device] || [];
//     const updatedFiles = [...currentFiles, ...acceptedFiles];

//     const updatedFilesMap = {
//       ...files,
//       [device]: updatedFiles,
//     };

//     if (onFilesChange) {
//       onFilesChange(updatedFilesMap);
//     }
//   };

//   const removeExistingImage = (imageIndex, device) => {
//     // Filter out the image at index for this device
//     const updatedValue = value
//       .map((item, idx) => {
//         if (idx === imageIndex) {
//           const newItem = {...item};
//           delete newItem[device];
//           return newItem;
//         }
//         return item;
//       })
//       .filter(item => Object.keys(item).length > 0); // Remove empty items

//     onChange(updatedValue);
//   };

//   const removeNewImage = (fileIndex, device) => {
//     const currentFiles = files[device] || [];
//     const updatedFiles = currentFiles.filter((_, i) => i !== fileIndex);

//     const updatedFilesMap = {
//       ...files,
//       [device]: updatedFiles,
//     };
//     onFilesChange(updatedFilesMap);
//   };

//   return (
//     <Box>
//       <Box
//         display="flex"
//         justifyContent="space-between"
//         alignItems="center"
//         mb={1}>
//         <MDTypography variant="caption" color={error ? 'error' : 'text'}>
//           {label}
//         </MDTypography>
//       </Box>

//       {/* Device Tabs */}
//       <DeviceTabs
//         activeDevice={activeDevice}
//         onDeviceChange={setActiveDevice}
//       />

//       {/* Upload Area */}
//       <Dropzone
//         onDrop={files => handleDrop(files, activeDevice)}
//         accept={{'image/*': []}}
//         multiple>
//         {({getRootProps, getInputProps}) => (
//           <Paper
//             {...getRootProps()}
//             sx={{
//               p: 2,
//               border: '2px dashed',
//               borderColor: error ? 'error.main' : 'grey.300',
//               textAlign: 'center',
//               cursor: 'pointer',
//               '&:hover': {borderColor: 'primary.main'},
//               mb: 2,
//             }}>
//             <input {...getInputProps()} />
//             <Add sx={{fontSize: 40, color: 'text.secondary'}} />
//             <MDTypography variant="body2" color="text">
//               Add {activeDevice} images
//             </MDTypography>
//           </Paper>
//         )}
//       </Dropzone>

//       {/* Existing Images */}
//       {value.length > 0 && (
//         <Box mb={2}>
//           <MDTypography variant="caption" color="textSecondary" gutterBottom>
//             Existing Images:
//           </MDTypography>
//           <Grid container spacing={1}>
//             {value.map((item, index) => (
//               <Grid item xs={4} sm={3} md={2} key={index}>
//                 <Paper sx={{p: 0.5, position: 'relative'}}>
//                   {item[activeDevice] ? (
//                     <>
//                       <img
//                         src={item[activeDevice]}
//                         alt=""
//                         style={{
//                           width: '100%',
//                           height: 80,
//                           objectFit: 'cover',
//                           borderRadius: 4,
//                         }}
//                       />
//                       <IconButton
//                         size="small"
//                         color="error"
//                         onClick={() => removeExistingImage(index, activeDevice)}
//                         sx={{
//                           position: 'absolute',
//                           top: 2,
//                           right: 2,
//                           bgcolor: 'rgba(255,255,255,0.9)',
//                         }}>
//                         <Delete fontSize="small" />
//                       </IconButton>
//                     </>
//                   ) : (
//                     <Box
//                       sx={{
//                         width: '100%',
//                         height: 80,
//                         display: 'flex',
//                         alignItems: 'center',
//                         justifyContent: 'center',
//                         bgcolor: 'grey.100',
//                         borderRadius: 1,
//                       }}>
//                       <MDTypography variant="caption" color="textSecondary">
//                         No {activeDevice}
//                       </MDTypography>
//                     </Box>
//                   )}
//                 </Paper>
//               </Grid>
//             ))}
//           </Grid>
//         </Box>
//       )}

//       {/* New Uploads */}
//       {files[activeDevice]?.length > 0 && (
//         <Box>
//           <MDTypography variant="caption" color="success" gutterBottom>
//             New {activeDevice} images (to be uploaded):
//           </MDTypography>
//           <Grid container spacing={1}>
//             {files[activeDevice].map((file, index) => (
//               <Grid item xs={4} sm={3} md={2} key={index}>
//                 <Paper sx={{p: 0.5, position: 'relative'}}>
//                   <img
//                     src={filePreviews[activeDevice]?.[index]}
//                     alt=""
//                     style={{
//                       width: '100%',
//                       height: 80,
//                       objectFit: 'cover',
//                       borderRadius: 4,
//                     }}
//                   />
//                   <IconButton
//                     size="small"
//                     color="error"
//                     onClick={() => removeNewImage(index, activeDevice)}
//                     sx={{
//                       position: 'absolute',
//                       top: 2,
//                       right: 2,
//                       bgcolor: 'rgba(255,255,255,0.9)',
//                     }}>
//                     <Delete fontSize="small" />
//                   </IconButton>
//                 </Paper>
//               </Grid>
//             ))}
//           </Grid>
//         </Box>
//       )}

//       {error && (
//         <MDTypography
//           variant="caption"
//           color="error"
//           sx={{mt: 0.5, display: 'block'}}>
//           {helperText}
//         </MDTypography>
//       )}
//     </Box>
//   );
// };

// export default DeviceGalleryUploader;

// OLD .....

// import {Box, Grid, IconButton, Paper} from '@mui/material';
// import MDTypography from 'components/MDTypography';
// import {useEffect, useState} from 'react';
// import DeviceTabs from './DeviceTabs';
// import Dropzone from 'react-dropzone';
// import {Add, Delete} from '@mui/icons-material';

// const DeviceGalleryUploader = ({
//   value = [],
//   onChange,
//   label,
//   files = {},
//   onFilesChange,
//   error,
//   helperText,
//   deviceTypes = ['desktop'],
// }) => {
//   const [activeDevice, setActiveDevice] = useState(deviceTypes[0] || 'desktop');
//   const [filePreviews, setFilePreviews] = useState({});

//   useEffect(() => {
//     const previews = {};
//     Object.entries(files).forEach(([device, fileList]) => {
//       if (fileList && fileList.length && deviceTypes.includes(device)) {
//         previews[device] = fileList.map(file => URL.createObjectURL(file));
//       }
//     });
//     setFilePreviews(previews);

//     return () => {
//       Object.values(previews).forEach(devicePreviews => {
//         devicePreviews?.forEach(url => URL.revokeObjectURL(url));
//       });
//     };
//   }, [files, deviceTypes]);

//   const handleDrop = (acceptedFiles, device) => {
//     if (!acceptedFiles.length) return;

//     const currentFiles = files[device] || [];
//     const updatedFiles = [...currentFiles, ...acceptedFiles];

//     const updatedFilesMap = {
//       ...files,
//       [device]: updatedFiles,
//     };

//     if (onFilesChange) {
//       onFilesChange(updatedFilesMap);
//     }
//   };

//   const removeExistingImage = (imageIndex, device) => {
//     const updatedValue = value
//       .map((item, idx) => {
//         if (idx === imageIndex) {
//           const newItem = {...item};
//           delete newItem[device];
//           return newItem;
//         }
//         return item;
//       })
//       .filter(item => Object.keys(item).length > 0);

//     onChange(updatedValue);
//   };

//   const removeNewImage = (fileIndex, device) => {
//     const currentFiles = files[device] || [];
//     const updatedFiles = currentFiles.filter((_, i) => i !== fileIndex);

//     const updatedFilesMap = {
//       ...files,
//       [device]: updatedFiles,
//     };
//     onFilesChange(updatedFilesMap);
//   };

//   const filteredValue = value.filter(item => item[activeDevice]);

//   return (
//     <Box>
//       <Box
//         display="flex"
//         justifyContent="space-between"
//         alignItems="center"
//         mb={1}>
//         <MDTypography variant="caption" color={error ? 'error' : 'text'}>
//           {label}
//         </MDTypography>
//       </Box>

//       {deviceTypes.length > 1 ? (
//         <DeviceTabs
//           activeDevice={activeDevice}
//           onDeviceChange={setActiveDevice}
//           deviceTypes={deviceTypes}
//         />
//       ) : (
//         <Box sx={{mb: 1}}>
//           <MDTypography variant="caption" color="textSecondary">
//             {deviceTypes[0] === 'desktop' ? '🖥️ Desktop Images' :
//              deviceTypes[0] === 'mobile' ? '📱 Mobile Images' :
//              deviceTypes[0] === 'tablet' ? '💻 Tablet Images' : 'Images'}
//           </MDTypography>
//         </Box>
//       )}

//       <Dropzone
//         onDrop={files => handleDrop(files, activeDevice)}
//         accept={{'image/*': []}}
//         multiple>
//         {({getRootProps, getInputProps}) => (
//           <Paper
//             {...getRootProps()}
//             sx={{
//               p: 2,
//               border: '2px dashed',
//               borderColor: error ? 'error.main' : 'grey.300',
//               textAlign: 'center',
//               cursor: 'pointer',
//               '&:hover': {borderColor: 'primary.main'},
//               mb: 2,
//             }}>
//             <input {...getInputProps()} />
//             <Add sx={{fontSize: 40, color: 'text.secondary'}} />
//             <MDTypography variant="body2" color="text">
//               Add {deviceTypes.length > 1 ? activeDevice : ''} images
//             </MDTypography>
//           </Paper>
//         )}
//       </Dropzone>

//       {filteredValue.length > 0 && (
//         <Box mb={2}>
//           <MDTypography variant="caption" color="textSecondary" gutterBottom>
//             Existing Images:
//           </MDTypography>
//           <Grid container spacing={1}>
//             {value.map((item, index) => (
//               item[activeDevice] && (
//                 <Grid item xs={4} sm={3} md={2} key={index}>
//                   <Paper sx={{p: 0.5, position: 'relative'}}>
//                     <img
//                       src={item[activeDevice]}
//                       alt=""
//                       style={{
//                         width: '100%',
//                         height: 80,
//                         objectFit: 'cover',
//                         borderRadius: 4,
//                       }}
//                     />
//                     <IconButton
//                       size="small"
//                       color="error"
//                       onClick={() => removeExistingImage(index, activeDevice)}
//                       sx={{
//                         position: 'absolute',
//                         top: 2,
//                         right: 2,
//                         bgcolor: 'rgba(255,255,255,0.9)',
//                       }}>
//                       <Delete fontSize="small" />
//                     </IconButton>
//                   </Paper>
//                 </Grid>
//               )
//             ))}
//           </Grid>
//         </Box>
//       )}

//       {files[activeDevice]?.length > 0 && (
//         <Box>
//           <MDTypography variant="caption" color="success" gutterBottom>
//             New {deviceTypes.length > 1 ? activeDevice : ''} images (to be uploaded):
//           </MDTypography>
//           <Grid container spacing={1}>
//             {files[activeDevice].map((file, index) => (
//               <Grid item xs={4} sm={3} md={2} key={index}>
//                 <Paper sx={{p: 0.5, position: 'relative'}}>
//                   <img
//                     src={filePreviews[activeDevice]?.[index]}
//                     alt=""
//                     style={{
//                       width: '100%',
//                       height: 80,
//                       objectFit: 'cover',
//                       borderRadius: 4,
//                     }}
//                   />
//                   <IconButton
//                     size="small"
//                     color="error"
//                     onClick={() => removeNewImage(index, activeDevice)}
//                     sx={{
//                       position: 'absolute',
//                       top: 2,
//                       right: 2,
//                       bgcolor: 'rgba(255,255,255,0.9)',
//                     }}>
//                     <Delete fontSize="small" />
//                   </IconButton>
//                 </Paper>
//               </Grid>
//             ))}
//           </Grid>
//         </Box>
//       )}

//       {error && (
//         <MDTypography
//           variant="caption"
//           color="error"
//           sx={{mt: 0.5, display: 'block'}}>
//           {helperText}
//         </MDTypography>
//       )}
//     </Box>
//   );
// };

// export default DeviceGalleryUploader;

// ------------------------------------------------------------------------------------------------------------------------------------

// import {Box, Grid, IconButton, Paper} from '@mui/material';
// import MDTypography from 'components/MDTypography';
// import {useEffect, useState} from 'react';
// import DeviceTabs from './DeviceTabs';
// import Dropzone from 'react-dropzone';
// import {Add, Delete} from '@mui/icons-material';

// const DeviceGalleryUploader = ({
//   value = [],
//   onChange,
//   label,
//   files = {},
//   onFilesChange,
//   error,
//   helperText,
//   deviceTypes = ['desktop'],
// }) => {
//   const [activeDevice, setActiveDevice] = useState(deviceTypes[0] || 'desktop');
//   const [filePreviews, setFilePreviews] = useState({});
//   const [localFiles, setLocalFiles] = useState({});

//   useEffect(() => {
//     return () => {
//       Object.values(filePreviews).forEach(devicePreviews => {
//         devicePreviews?.forEach(url => {
//           if (url?.startsWith('blob:')) {
//             URL.revokeObjectURL(url);
//           }
//         });
//       });
//     };
//   }, []);

//   useEffect(() => {
//     if (files && Object.keys(files).length > 0) {
//       setLocalFiles(files);
//     }
//   }, []);

//   useEffect(() => {
//     const previews = {};
//     Object.entries(localFiles).forEach(([device, fileList]) => {
//       if (fileList && fileList.length && deviceTypes.includes(device)) {
//         previews[device] = fileList.map(file => {
//           if (typeof file === 'string') return file;
//           return URL.createObjectURL(file);
//         });
//       }
//     });
//     setFilePreviews(previews);
//   }, [localFiles]);

//   const handleDrop = (acceptedFiles, device) => {
//     if (!acceptedFiles.length) return;

//     const currentFiles = localFiles[device] || [];
//     const updatedFiles = [...currentFiles, ...acceptedFiles];

//     const updatedFilesMap = {
//       ...localFiles,
//       [device]: updatedFiles,
//     };

//     setLocalFiles(updatedFilesMap);

//     if (onFilesChange) {
//       onFilesChange(updatedFilesMap);
//     }
//   };

//   const removeExistingImage = (imageIndex, device) => {
//     const updatedValue = value.filter((_, idx) => idx !== imageIndex);
//     onChange(updatedValue);
//   };

//   const removeNewImage = (fileIndex, device) => {
//     const currentFiles = localFiles[device] || [];

//     if (filePreviews[device]?.[fileIndex]?.startsWith('blob:')) {
//       URL.revokeObjectURL(filePreviews[device][fileIndex]);
//     }

//     const updatedFiles = currentFiles.filter((_, i) => i !== fileIndex);

//     const updatedFilesMap = {
//       ...localFiles,
//       [device]: updatedFiles,
//     };

//     setLocalFiles(updatedFilesMap);

//     if (onFilesChange) {
//       onFilesChange(updatedFilesMap);
//     }
//   };

//   const displayImages = deviceTypes.length === 1
//     ? value
//     : value.filter(item => item[activeDevice]);

//   return (
//     <Box>
//       <Box
//         display="flex"
//         justifyContent="space-between"
//         alignItems="center"
//         mb={1}>
//         <MDTypography variant="caption" color={error ? 'error' : 'text'}>
//           {label}
//         </MDTypography>
//       </Box>

//       {deviceTypes.length > 1 ? (
//         <DeviceTabs
//           activeDevice={activeDevice}
//           onDeviceChange={setActiveDevice}
//           deviceTypes={deviceTypes}
//         />
//       ) : (
//         <Box sx={{mb: 1}}>
//           <MDTypography variant="caption" color="textSecondary">
//             {deviceTypes[0] === 'desktop' ? '🖥️ Desktop Images' :
//              deviceTypes[0] === 'mobile' ? '📱 Mobile Images' :
//              deviceTypes[0] === 'tablet' ? '💻 Tablet Images' : 'Images'}
//           </MDTypography>
//         </Box>
//       )}

//       <Dropzone
//         onDrop={droppedFiles => handleDrop(droppedFiles, activeDevice)}
//         accept={{'image/*': []}}
//         multiple>
//         {({getRootProps, getInputProps}) => (
//           <Paper
//             {...getRootProps()}
//             sx={{
//               p: 2,
//               border: '2px dashed',
//               borderColor: error ? 'error.main' : 'grey.300',
//               textAlign: 'center',
//               cursor: 'pointer',
//               '&:hover': {borderColor: 'primary.main'},
//               mb: 2,
//               bgcolor: error ? 'error.light' : 'transparent',
//             }}>
//             <input {...getInputProps()} />
//             <Add sx={{fontSize: 40, color: 'text.secondary'}} />
//             <MDTypography variant="body2" color="text">
//               Add {deviceTypes.length > 1 ? activeDevice : ''} images
//             </MDTypography>
//             <MDTypography variant="caption" color="textSecondary">
//               Drag & drop or click to upload multiple images
//             </MDTypography>
//           </Paper>
//         )}
//       </Dropzone>

//       {displayImages.length > 0 && (
//         <Box mb={2}>
//           <MDTypography variant="caption" color="textSecondary" gutterBottom>
//             Existing Images ({displayImages.length})
//           </MDTypography>
//           <Grid container spacing={1}>
//             {displayImages.map((item, index) => {
//               const imageUrl = deviceTypes.length === 1
//                 ? item
//                 : item[activeDevice];

//               if (!imageUrl) return null;

//               return (
//                 <Grid item xs={4} sm={3} md={2} key={index}>
//                   <Paper sx={{p: 0.5, position: 'relative'}}>
//                     <img
//                       src={imageUrl}
//                       alt={`Gallery ${index + 1}`}
//                       style={{
//                         width: '100%',
//                         height: 80,
//                         objectFit: 'cover',
//                         borderRadius: 4,
//                       }}
//                     />
//                     <IconButton
//                       size="small"
//                       color="error"
//                       onClick={() => removeExistingImage(index, activeDevice)}
//                       sx={{
//                         position: 'absolute',
//                         top: 2,
//                         right: 2,
//                         bgcolor: 'rgba(255,255,255,0.9)',
//                         '&:hover': {bgcolor: 'rgba(255,255,255,1)'},
//                       }}>
//                       <Delete fontSize="small" />
//                     </IconButton>
//                   </Paper>
//                 </Grid>
//               );
//             })}
//           </Grid>
//         </Box>
//       )}

//       {localFiles[activeDevice]?.length > 0 && (
//         <Box>
//           <MDTypography variant="caption" color="success" gutterBottom>
//             New {deviceTypes.length > 1 ? activeDevice : ''} images (
//             {localFiles[activeDevice].length} ready to upload)
//           </MDTypography>
//           <Grid container spacing={1}>
//             {localFiles[activeDevice].map((file, index) => (
//               <Grid item xs={4} sm={3} md={2} key={index}>
//                 <Paper sx={{p: 0.5, position: 'relative'}}>
//                   <img
//                     src={filePreviews[activeDevice]?.[index]}
//                     alt={`New ${index + 1}`}
//                     style={{
//                       width: '100%',
//                       height: 80,
//                       objectFit: 'cover',
//                       borderRadius: 4,
//                     }}
//                   />
//                   <IconButton
//                     size="small"
//                     color="error"
//                     onClick={() => removeNewImage(index, activeDevice)}
//                     sx={{
//                       position: 'absolute',
//                       top: 2,
//                       right: 2,
//                       bgcolor: 'rgba(255,255,255,0.9)',
//                       '&:hover': {bgcolor: 'rgba(255,255,255,1)'},
//                     }}>
//                     <Delete fontSize="small" />
//                   </IconButton>
//                   {/* <Box
//                     sx={{
//                       position: 'absolute',
//                       bottom: 2,
//                       left: 2,
//                       right: 2,
//                       bgcolor: 'rgba(0,0,0,0.7)',
//                       color: 'white',
//                       fontSize: '10px',
//                       padding: '2px 4px',
//                       borderRadius: 1,
//                       textOverflow: 'ellipsis',
//                       overflow: 'hidden',
//                       whiteSpace: 'nowrap',
//                     }}>
//                     {file.name}
//                   </Box> */}
//                 </Paper>
//               </Grid>
//             ))}
//           </Grid>
//         </Box>
//       )}

//       {displayImages.length === 0 && !localFiles[activeDevice]?.length && (
//         <Paper sx={{p: 2, textAlign: 'center', bgcolor: 'grey.50'}}>
//           <MDTypography variant="body2" color="textSecondary">
//             No images yet. Click above to upload.
//           </MDTypography>
//         </Paper>
//       )}

//       {error && (
//         <MDTypography
//           variant="caption"
//           color="error"
//           sx={{mt: 0.5, display: 'block'}}>
//           {helperText}
//         </MDTypography>
//       )}
//     </Box>
//   );
// };

// export default DeviceGalleryUploader;

// ----------------------------------------------------------------------------------------------------------
